import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  createCustomerMapData,
  deleteCustomerMapData,
  listCustomerMaps,
  updateCustomerMapData,
  listUnmappedCustomers
} from '../Api/CustomerMap/CustomerMapApi';
import { addError, clearError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { DrawerData } from './Drawer';

export interface CustomerMap {
  customerId: number;
  customerName: string;
  sfdcCustomerUid: string;
  sfdcCustomerSfId: string;
  sfdcCustomerName: string;
  sfdcUltimateParentAccountId: string;
  sfdcUltimateParentAccountSfId: string;
  sfdcUltimateParentAccountName: string;
}

export interface UnmappedCustomer {
  customerId: number;
  customerName: string;
  accountCount: number;
  latestRunYearMonth: string;
}

const customerMapsSubject = new BehaviorSubject<PaginatedResponse<CustomerMap>>({} as PaginatedResponse<CustomerMap>);
const unmappedCustomerSubject = new BehaviorSubject<PaginatedResponse<UnmappedCustomer>>(
  {} as PaginatedResponse<UnmappedCustomer>
);
const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const inboundFetchNumberUnmappedSubject = new BehaviorSubject<number>(0);
const customerMapsLoadingSubject = new BehaviorSubject<boolean>(false);
const unmappedCustomersLoadingSubject = new BehaviorSubject<boolean>(false);
const filtersSubject = new BehaviorSubject<ApiAdvancedFilter | undefined>(undefined);
const unmappedFiltersSubject = new BehaviorSubject<ApiAdvancedFilter | undefined>(undefined);
const customerMapDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);

export const getCustomerMapsSubject = (): BehaviorSubject<PaginatedResponse<CustomerMap>> => customerMapsSubject;
export const getUnmappedCustomersSubject = (): BehaviorSubject<PaginatedResponse<UnmappedCustomer>> =>
  unmappedCustomerSubject;
export const getCustomerMapsLoadingSubject = (): BehaviorSubject<boolean> => customerMapsLoadingSubject;
export const getUnmappedCustomersLoadingSubject = (): BehaviorSubject<boolean> => unmappedCustomersLoadingSubject;
export const getCustomerMapDetailSubject = (): BehaviorSubject<DrawerData | undefined> => customerMapDetailSubject;

export const createCustomerMap = async (customerMap: Partial<CustomerMap>) => {
  const customerMapData: CustomerMap = { ...({} as CustomerMap), ...customerMap };

  const { data, error } = await createCustomerMapData(customerMapData);

  if (data) {
    clearError('customer-map');
    fetchCustomerMaps(filtersSubject.getValue(), true);
    fetchUnmappedCustomers(unmappedFiltersSubject.getValue(), true);
  }
  if (error) addError('customer-map', error.message);

  return { data, error };
};

export const fetchCustomerMaps = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  filtersSubject.next(filters);
  inboundFetchNumberSubject.next(newFetchNumber);
  if (customerMapsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  customerMapsLoadingSubject.next(true);

  try {
    const { data, error } = await listCustomerMaps(filters);
    const currentFetchNumber = inboundFetchNumberSubject.getValue();
    if (data && newFetchNumber === currentFetchNumber) {
      clearError('customer-map');
      customerMapsSubject.next(data);
    }
    if (error) addError('customer-map', error.message);
  } finally {
    // Without this, anything thrown above strands the tab on its spinner with no way back.
    newFetchNumber === inboundFetchNumberSubject.getValue() && customerMapsLoadingSubject.next(false);
  }
};

export const fetchUnmappedCustomers = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberUnmappedSubject.getValue() + 1;
  unmappedFiltersSubject.next(filters);
  inboundFetchNumberUnmappedSubject.next(newFetchNumber);
  if (unmappedCustomerSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  unmappedCustomersLoadingSubject.next(true);

  const { data, error } = await listUnmappedCustomers(filters);
  const currentFetchNumber = inboundFetchNumberUnmappedSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError('customer-map');
    unmappedCustomerSubject.next(data);
  }
  if (error) addError('customer-map', error.message);

  newFetchNumber === currentFetchNumber && unmappedCustomersLoadingSubject.next(false);
};

export const updateCustomerMap = async (update: CustomerMap) => {
  const { data, error } = await updateCustomerMapData(update);
  if (data) {
    clearError('customer-map');
    fetchCustomerMaps(filtersSubject.getValue(), true);
    customerMapDetailSubject.next(data);
  }
  if (error) addError('customer-map', error.message);

  return { data, error };
};

export const deleteCustomerMap = async (customerId: number) => {
  const { error } = await deleteCustomerMapData(customerId);

  if (error) {
    addError('customer-map', error.message);
    return;
  }

  clearError('customer-map');
  fetchCustomerMaps(filtersSubject.getValue(), true);
};
